// Task progress from area completion — the pure derivation the clients'
// progress sessions (`client/activities` locationProgress) and the server's
// area writes share. Not under `client/`: the backend derives the % an area
// write moves from it (QA R-04 — the % is stored only once the areas are).
//
// A leaf task's % is area-driven: from a base %, the areas not yet complete at
// the base split the REMAINING percentage equally, so completing them all
// lands on exactly 100. E.g. 2 of 10 done at 30 % — each of the 8 remaining
// areas is worth (100 − 30) / 8 = 8.75 %.

import type { AreaStatus } from './index.js';

/**
 * Plain area-driven progress as a 0–100 integer, or `null` when the task has
 * no assigned areas (nothing to derive from — the manual slider is then the
 * only signal). `completed` is clamped to `total` defensively.
 */
export function areaProgress(completed: number, total: number): number | null {
  if (total <= 0) return null;
  const done = Math.max(0, Math.min(completed, total));
  return Math.round((done / total) * 100);
}

/**
 * Progress after area changes relative to a drawer-open snapshot: `base` (the
 * stored % at open) plus/minus an equal share of the remaining percentage per
 * area completed/uncompleted since open. The share is
 * `(100 − base) / (total − baseCompleted)` — the areas not yet complete at
 * open split what's left, so completing them all always lands on exactly 100.
 *
 * `null` when the task has no areas. When there is no remaining share to split
 * — every area was already complete at open, or `base` is already 100 (e.g. a
 * force-completed task whose areas lag behind) — falls back to plain
 * {@link areaProgress} so area edits still move the number. Without the
 * `base >= 100` fallback the share is 0 and the task freezes at 100 %: taps
 * can't lower it, so the 100 ⟹ completed coupling can never reopen it.
 */
export function blendProgress(
  base: number,
  baseCompleted: number,
  completed: number,
  total: number,
): number | null {
  if (total <= 0) return null;
  const done = Math.max(0, Math.min(completed, total));
  const remainingAreas = total - baseCompleted;
  if (remainingAreas <= 0 || base >= 100) return areaProgress(done, total);
  const share = (100 - base) / remainingAreas;
  const pct = base + (done - baseCompleted) * share;
  return Math.round(Math.max(0, Math.min(100, pct)));
}

/** How many of these areas are reported done. */
export function countDone(locations: readonly { status: AreaStatus }[]): number {
  return locations.filter((location) => location.status === 'complete').length;
}

/**
 * The task % after a report took the done count from `doneBefore` to
 * `doneAfter` ({@link blendProgress} from the session base) — or `null`, no
 * progress write, when it didn't move: only done areas move the %, so a review
 * submission, its undo and a reject leave it alone (area review stage spec,
 * ruling 8).
 */
export function progressAfterReport(
  base: number,
  baseCompleted: number,
  doneBefore: number,
  doneAfter: number,
  total: number,
): number | null {
  return doneAfter === doneBefore ? null : blendProgress(base, baseCompleted, doneAfter, total);
}
